import React, { useState, useEffect } from "react";
import { Box, Typography, Card, TextField, Button, Grid, Link } from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../../globalState/hooks";
import { useTranslation } from "react-i18next";
import style from "../workSheet.module.css";
import WorksheetButtons from "../../../components/worksheetButtons";
import { CalculateFinalValueForDeviation } from "../../../utils/calculationForGrossAdjustedValueForDeviation";
import { calculateBCSOShares } from "../../../utils/calculateBCSOShares";
import { updateBasicInfo } from "../../../globalState/actions/create-worksheet-action";
import { getScheduleCFinalObligation } from "../../../utils/getScheduleCCalculate";
import CustomModal from "../../../components/customModal";
import ReportTechnicalForm from "../../../components/reportTechnicalIssue";
import SettingIcons from "../../../assets/images/SettingIcon.svg";


const ScheduleCTab: React.FC = () => {
  const states = useAppSelector((state) => state.createWorksheet);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const noncustodialName = states.non_custodial_parent || "";
  const custodialName =
    noncustodialName === states.parent_name_1
      ? states.parent_name_2
      : states.parent_name_1;
  
  // Check if both parents are non-custodial (when non_custodial_parent contains both names)
  const isBothParentsNonCustodial = noncustodialName.includes(" and ");
  const nonCustodialDays = states.schedule_c_non_custodial_parenting_days || "";
  const custodialDays = states.schedule_c_custodial_parenting_days || "";
  const [nonCustodialShare, setNonCustodialShare] = useState<number>(0);
  const [custodialShare, setCustodialShare] = useState<number>(0);
  const [result, setResult] = useState<number | null>(null);
  const [error, setError] = useState<string>("");
  const [reportTechnicalModal, setReportTechnicalModal] = useState(false);
  const [inputCaptcha, setInputCaptcha] = useState("");
  const formRef = React.useRef<any>(null);
  const workSheetStates = states;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Initialize result from stored value when component mounts
  useEffect(() => {
    if (states.schedule_c_final_obligation) {
      setResult(Number(states.schedule_c_final_obligation));
    }
  }, [states.schedule_c_final_obligation]);

  // Clear parenting days when non-custodial parent becomes invalid
  useEffect(() => {
    if (isBothParentsNonCustodial || !noncustodialName) {
      if (nonCustodialDays !== "0" || custodialDays !== "0") {
        dispatch(updateBasicInfo('schedule_c_non_custodial_parenting_days', "0"));
        dispatch(updateBasicInfo('schedule_c_custodial_parenting_days', "0"));
        dispatch(updateBasicInfo('schedule_c_final_obligation', "0"));
        setResult(0);
      }
    }
  }, [isBothParentsNonCustodial, noncustodialName]);

  const handleReportModalOpen = () => setReportTechnicalModal(true);
  const handleReportModalClose = () => setReportTechnicalModal(false);
  const handleSaveData = () => {};
  const handleSubmitTechnicalReport = () => {};

  // Calculate BCSO shares and auto-recalculate final obligation when income data changes
  useEffect(() => {
    const preexistingValue1 =
      states.pre_existing_child_support?.reduce((acc, item) => {
        return acc + Number(item.preexisting_child_support_paid_monthly1);
      }, 0) || 0;

    const preexistingValue2 =
      states.pre_existing_child_support?.reduce((acc, item) => {
        return acc + Number(item.preexisting_child_support_paid_monthly2);
      }, 0) || 0;

    const ChildValues = states?.proposed_adjustment_for_qualified_child
      ? states.proposed_adjustment_for_qualified_child.reduce(
          (
            acc: { parent1: number; parent2: number },
            item: { adjustment_requested_by: string }
          ) => {
            if (item.adjustment_requested_by === states.parent_name_1) {
              acc.parent1 = (acc.parent1 || 0) + 1;
            }

            if (item.adjustment_requested_by === states.parent_name_2) {
              acc.parent2 = (acc.parent2 || 0) + 1;
            }

            return acc;
          },
          { parent1: 0, parent2: 0 }
        )
      : { parent1: 0, parent2: 0 };

    const AdjustedIncome1 = CalculateFinalValueForDeviation(
      Number(states?.total_gross_income1),
      Number(states?.monthly_self_employment_income1),
      Number(preexistingValue1),
      ChildValues.parent1,
      states.created_at
    );

    const AdjustedIncome2 = CalculateFinalValueForDeviation(
      Number(states?.total_gross_income2),
      Number(states?.monthly_self_employment_income2),
      Number(preexistingValue2),
      ChildValues.parent2,
      states.created_at
    );

    const ChildCountNo =
      states?.add_child_to_worksheet?.filter(
        (child) => child.child_status === "included"
      ).length || 0;

    // Determine custodial and non-custodial adjusted incomes
    const custodialAdjustedIncome = states.non_custodial_parent === states.parent_name_1 
      ? AdjustedIncome2 : AdjustedIncome1;
    const nonCustodialAdjustedIncome = states.non_custodial_parent === states.parent_name_1 
      ? AdjustedIncome1 : AdjustedIncome2;
    
    // Calculate BCSO shares using utility function
    const bcsoShares = calculateBCSOShares(
      custodialAdjustedIncome,
      nonCustodialAdjustedIncome,
      ChildCountNo,
      states.created_at
    );
    
    setNonCustodialShare(bcsoShares.nonCustodialShare);
    setCustodialShare(bcsoShares.custodialShare);
    
    // Dispatch BCSO shares to Redux
    dispatch(updateBasicInfo('schedule_c_custodial_share', bcsoShares.custodialShare));
    dispatch(updateBasicInfo('schedule_c_non_custodial_share', bcsoShares.nonCustodialShare));
    
    // Auto-recalculate final obligation if parenting days are set
    const nonCustodialDays = Number(states.schedule_c_non_custodial_parenting_days) || 0;
    const custodialDays = Number(states.schedule_c_custodial_parenting_days) || 0;
    
    if (nonCustodialDays === 0 || custodialDays === 0) {
      // If either is 0, set final obligation to 0
      setResult(0);
      dispatch(updateBasicInfo('schedule_c_final_obligation', 0));
    } else if (nonCustodialDays > 0 && custodialDays > 0 && nonCustodialDays + custodialDays === 365) {
      const finalObligation = getScheduleCFinalObligation(states);
      setResult(finalObligation);
      dispatch(updateBasicInfo('schedule_c_final_obligation', finalObligation));
    }
  }, [states.total_gross_income1, states.total_gross_income2, states.monthly_self_employment_income1, states.monthly_self_employment_income2, states.pre_existing_child_support, states.proposed_adjustment_for_qualified_child, states.add_child_to_worksheet, states.parent_name_1, states.parent_name_2, states.non_custodial_parent, states.created_at, states.schedule_c_non_custodial_parenting_days, states.schedule_c_custodial_parenting_days, dispatch]);

  const calculate = () => {
    setError("");

    if (Number(nonCustodialDays) === 0 || Number(custodialDays) === 0) {
      setResult(0);
      dispatch(updateBasicInfo('schedule_c_final_obligation', 0));
      return;
    }

    if (Number(nonCustodialDays) + Number(custodialDays) !== 365) {
      setError(
        "The sum of custodial and non-custodial parenting days must equal 365 days."
      );
      setResult(null);
      return;
    }
    //    A	Non-custodial parent’s overnights
    //    B	Custodial parent’s overnights
    //    BS	Non-custodial parent’s share of BCSO
    //    AS	Custodial parent’s share of BCSO
    // Formula: ((A^2.5 * AS) - (B^2.5 * BS)) / (A^2.5 + B^2.5)
    // const numerator = Math.pow(A, 2.5) * BS - Math.pow(B, 2.5) * AS;
    const finalObligation = getScheduleCFinalObligation(states);
    setResult(finalObligation);
    
    // Dispatch final obligation to Redux
    dispatch(updateBasicInfo('schedule_c_final_obligation', finalObligation));
  };

  return (
    <>
      <Box sx={{ pt: 2 }}>
        <Card sx={{ mb: 2 }}>
          {/* <Typography variant="subtitle1" gutterBottom>
            {t("Schedule C Information")}
          </Typography> */}
          <Typography>
            {/* {t("This is the new Schedule C tab content.")} */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                minHeight: "100vh",
                padding: "2rem",
                backgroundColor: "#f5f5f5",
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: "900px",
                  fontFamily:
                    "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #f1f3f4",
                  padding: "2rem",
                }}
              >
                <h2
                  style={{
                    textAlign: "center",
                    color: "#1a202c",
                    marginBottom: "2rem",
                    fontSize: "24px",
                    fontWeight: "700",
                    letterSpacing: "-0.025em",
                  }}
                >
                  {t(
                    "Georgia Parenting Time Adjustment (2026)"
                    )}
                </h2>

                {/* Calendar available as standalone tool via navbar */}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "30px",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      padding: "20px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "12px",
                      border: "2px solid #e2e8f0",
                    }}
                  >
                    <h3
                      style={{
                        textAlign: "center",
                        color: "#2d3748",
                        marginBottom: "20px",
                        fontSize: "18px",
                        fontWeight: "600",
                      }}
                    >
                      {t(
                        "Custodial Parent"
                        )}
                    </h3>

                    <div style={{ display: "grid", gap: "15px" }}>
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "6px",
                            fontWeight: "500",
                            color: "#4a5568",
                            fontSize: "14px",
                          }}
                        >
                          {t(
                            "Name"
                            )}:
                        </label>
                        <input
                          type="text"
                          value={isBothParentsNonCustodial ? "" : custodialName}
                          readOnly
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "2px solid #e1e5e9",
                            borderRadius: "6px",
                            fontSize: "14px",
                            outline: "none",
                            boxSizing: "border-box",
                            backgroundColor: "#f7fafc",
                          }}
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "6px",
                            fontWeight: "500",
                            color: "#4a5568",
                            fontSize: "14px",
                          }}
                        >
                          {t(
                            "Parenting Days"
                          )}: ({t(
                            "Auto-calculated"
                          )})
                        </label>
                        <input
                          type="number"
                          value={custodialDays}
                          readOnly
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "2px solid #e1e5e9",
                            borderRadius: "6px",
                            fontSize: "14px",
                            outline: "none",
                            boxSizing: "border-box",
                            backgroundColor: "#f7fafc",
                          }}
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "6px",
                            fontWeight: "500",
                            color: "#4a5568",
                            fontSize: "14px",
                          }}
                        >
                          {t(
                            "BCSO Share: (Auto-calculated)"
                          )}
                        </label>
                        <input
                          type="number"
                          value={isBothParentsNonCustodial ? "" : custodialShare.toFixed(2)}
                          readOnly
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "2px solid #e1e5e9",
                            borderRadius: "6px",
                            fontSize: "14px",
                            outline: "none",
                            boxSizing: "border-box",
                            backgroundColor: "#f7fafc",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "20px",
                      backgroundColor: "#f8fafc",
                      borderRadius: "12px",
                      border: "2px solid #e2e8f0",
                    }}
                  >
                    <h3
                      style={{
                        textAlign: "center",
                        color: "#2d3748",
                        marginBottom: "20px",
                        fontSize: "18px",
                        fontWeight: "600",
                      }}
                    >
                      {t(
                        "Noncustodial Parent"
                        )}
                    </h3>

                    <div style={{ display: "grid", gap: "15px" }}>
                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "6px",
                            fontWeight: "500",
                            color: "#4a5568",
                            fontSize: "14px",
                          }}
                        >
                          {t(
                            "Name"
                            )}:
                        </label>
                        <input
                          type="text"
                          value={isBothParentsNonCustodial ? "" : noncustodialName}
                          readOnly
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "2px solid #e1e5e9",
                            borderRadius: "6px",
                            fontSize: "14px",
                            outline: "none",
                            boxSizing: "border-box",
                            backgroundColor: "#f7fafc",
                          }}
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "6px",
                            fontWeight: "500",
                            color: "#4a5568",
                            fontSize: "14px",
                          }}
                        >
                          {t(
                            "Parenting Days"
                          )}
                          <sup>*</sup>:
                        </label>
                        <TextField
                          type="number"
                          value={nonCustodialDays}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            const numValue = Number(inputValue);
                            // Prevent negative numbers
                            if (inputValue !== "" && numValue < 0) {
                              return;
                            }
                            // Prevent values > 182.5 (non-custodial parent cannot exceed 182.5 days)
                            if (inputValue !== "" && numValue > 182.5) {
                              return;
                            }
                            dispatch(updateBasicInfo('schedule_c_non_custodial_parenting_days', inputValue));
                            if (inputValue !== "" && !isNaN(numValue) && numValue >= 0 && numValue <= 182.5) {
                              dispatch(updateBasicInfo('schedule_c_custodial_parenting_days', (365 - numValue).toString()));
                            } else if (inputValue === "") {
                              dispatch(updateBasicInfo('schedule_c_custodial_parenting_days', ""));
                            }
                          }}
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled={isBothParentsNonCustodial || !noncustodialName}
                          inputProps={{ min: 0 }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '14px',
                              backgroundColor: (isBothParentsNonCustodial || !noncustodialName) ? '#f7fafc' : '#FDFFA6'
                            }
                          }}
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "6px",
                            fontWeight: "500",
                            color: "#4a5568",
                            fontSize: "14px",
                          }}
                        >
                          {t(
                            "BCSO Share: (Auto-calculated)"
                          )}
                        </label>
                        <input
                          type="number"
                          value={isBothParentsNonCustodial ? "" : nonCustodialShare.toFixed(2)}
                          readOnly
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "2px solid #e1e5e9",
                            borderRadius: "6px",
                            fontSize: "14px",
                            outline: "none",
                            boxSizing: "border-box",
                            backgroundColor: "#f7fafc",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>

                {error && (
                    <div
                      style={{
                        marginTop: "20px",
                        padding: "20px",
                        backgroundColor: "#fed7d7",
                        border: "2px solid #fc8181",
                        borderRadius: "12px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#c53030",
                        }}
                      >
                        {error}
                      </div>
                    </div>
                  )}

                  {result !== null && (
                    <div
                      style={{
                        marginTop: "20px",
                        padding: "20px",
                        backgroundColor: "#f0fff4",
                        border: "2px solid #68d391",
                        borderRadius: "12px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: "#22543d",
                          marginBottom: "4px",
                        }}
                      >
                        {t(
                          "Parenting Time Adjustment for Noncustodial Parent"
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: "28px",
                          fontWeight: "800",
                          color: "#22543d",
                        }}
                      >
                        ${result.toFixed(2)} / {t("month")}
                      </div>
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: "20px",
                      padding: "16px",
                      backgroundColor: "#f7fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "14px",
                      color: "#4a5568",
                      fontStyle: "italic",
                      textAlign: "center",
                    }}
                  >
                    {t(
                      "This number does not represent the final child support amount. The Parenting Time Adjustment is shown on line 6 of the worksheet."
                    )}
                    <br/><br/>
                    {t(
                      "If there are multiple children with varying degrees of court ordered parenting time, use the average number of court ordered days."
                    )}
                    <br/><br/>
                    <sup>*</sup>
                    {t(
                      "Parenting days means the total number of overnights a parent spends with the child or in circumstances where a parent has shorter but regular and recurring daytime periods with a child, the total hours of parenting time in the annual average divided by 24 hours, including any hours spent overnight."
                    )}
                  </div>
                </div>
              <div style={{ textAlign: 'center' }}>
                <a href="/Parenting-Plan-Days.pdf" target="_blank" rel="noopener noreferrer">
                  {t(
                    "Click Here to View Sample Parenting Plan Day Counts."
                  )}
                </a>
              </div>
              </div>
            </div>
          </Typography>
        </Card>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <WorksheetButtons />
      </Box>
      <Grid item xs={12} md={12}>
        <Grid
          sx={{
            mb: 0,
            borderTop: "2px solid #DFDFDF",
            py: 2,
            px: 1,
            textDecoration: "underline",
            cursor: "pointer",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex">
            <img src={SettingIcons} alt="img" style={{ height: "15px" }} />
            <Link
              onClick={handleReportModalOpen}
              sx={{
                marginLeft: "5px",
                color: "#147AFF",
                textDecoration: "underline",
              }}
            >
              {t("Report Technical Issue")}
            </Link>

            <CustomModal
              open={reportTechnicalModal}
              onClose={handleReportModalClose}
              actionButtonLabel={t("Save")}
              title="Report Technical Issue"
              save={handleSaveData}
              borderBottom="0px"
              pb="0px"
            >
              <ReportTechnicalForm
                ref={formRef}
                inputCaptcha={inputCaptcha}
                handleSubmitTechnicalReport={handleSubmitTechnicalReport}
                states={workSheetStates}
              />
            </CustomModal>
          </Box>
          <Box sx={{ p: { xs: 2, md: 1 } }}>
            <Button
              className={style.globalButtonStyle}
              variant="outlined"
              onClick={() => {
                dispatch(updateBasicInfo("activeTab", "3"));
              }}
              sx={{ marginX: 2 }}
            >
              <span style={{ padding: "0 4px" }}>{"<"}</span> {t("Back Step")}
            </Button>
            <Button
              variant="contained"
              className={style.globalContainedButton}
              onClick={() => {
                dispatch(updateBasicInfo("activeTab", "5"));
              }}
            >
              {t("Next Step")} <span style={{ padding: "0 4px" }}>{">"}</span>
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default ScheduleCTab;
